import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sessionService } from '../services/SessionService';
import { Appointment } from '../models/Appointment';

const coerceDate = (d: any): Date =>
  d instanceof Date ? d : d?.toDate ? d.toDate() : new Date(d);

/**
 * The single "Start Session" action, shared by both entry points (RBT
 * Dashboard + My Sessions) so they behave identically (Phase 3 of
 * SESSION_UNIFICATION_PLAN.md).
 *
 * findOrCreate the canonical companies/{companyId}/sessions record — idempotent
 * by appointmentId, so tapping Start from either place (or re-entering) resolves
 * to the SAME record with no duplicates — then navigate to the active-session
 * screen. The SessionNotes mirror later findOrCreates the same doc, so nothing
 * is created twice.
 */
export function useBeginSession() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return useCallback(
    async (
      appt: Appointment,
      names: { clientName: string; supervisorName: string }
    ) => {
      if (!user) return;
      try {
        const clientAddress = [
          appt.location?.address,
          appt.location?.city,
          appt.location?.state,
        ]
          .filter(Boolean)
          .join(', ');
        await sessionService.findOrCreateByAppointment({
          companyId: user.companyId,
          appointmentId: appt.id,
          clientId: appt.clientId,
          clientName: names.clientName,
          rbtId: user.id,
          rbtName: user.name,
          supervisorId: appt.supervisorId || '',
          supervisorName: names.supervisorName || '',
          scheduledStart: coerceDate(appt.startTime),
          scheduledEnd: coerceDate(appt.endTime),
          clientAddress,
          sessionType: appt.supervisionRequired ? 'supervision' : 'direct-therapy',
        });
      } catch (e) {
        // Best-effort: even if the canonical write fails, still open the
        // screen — the mirror will findOrCreate the record there.
        console.error('beginSession error:', e);
      }
      navigate('/session-notes', { state: { appointmentId: appt.id } });
    },
    [user, navigate]
  );
}
